import { Trans } from '@lingui/macro'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material'
import { CoreService, VersionDTO } from '@om1/falcon-api'
import { useUserPermissions } from '@om1/platform-authentication'
import React, { useEffect, useState } from 'react'
import { fetchDataWithCache } from '../../../../apps/platform/src/query-utils'

interface VersionModalProps {
    open: boolean
    onClose: () => void
    appSemanticVersion: string
    jenkinsBuildNumber: string
    environmentTag: string
}

interface VersionInfoRowProps {
    label: React.ReactNode
    value: React.ReactNode
}

const VersionInfoRow: React.FC<VersionInfoRowProps> = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
        <Typography variant='body2' sx={{ fontWeight: 600, minWidth: '140px' }}>
            {label}:
        </Typography>
        <Typography variant='body2' sx={{ fontFamily: 'monospace', textAlign: 'right' }}>
            {value}
        </Typography>
    </Box>
)

export const VersionModal: React.FC<VersionModalProps> = ({ open, onClose, appSemanticVersion, jenkinsBuildNumber, environmentTag }) => {
    const { user } = useUserPermissions()
    const [backendVersion, setBackendVersion] = useState<string>('')
    const [alembicVersion, setAlembicVersion] = useState<string>('')
    const [analyticsDatabase, setAnalyticsDatabase] = useState<string>('')

    useEffect(() => {
        if (user && open) {
            const response: Promise<VersionDTO | undefined> = fetchDataWithCache('version', {}, CoreService.versionVersionGet)

            response.then((response) => {
                if (response) {
                    setBackendVersion(response.version)
                    setAlembicVersion(response.alembicVersion)
                    setAnalyticsDatabase(response.analyticsDatabase)
                }
            })
        }
    }, [user, open])

    let frontendVersion = 'develop'
    if (jenkinsBuildNumber !== 'develop') {
        frontendVersion = appSemanticVersion.replace(/(.*\.)(.*\.)(.*)/, `$1$2${jenkinsBuildNumber}`)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
                        <Trans>Version Information</Trans>
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size='small'
                        sx={{
                            color: 'grey.500',
                            '&:hover': {
                                backgroundColor: 'grey.100'
                            }
                        }}
                    >
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <VersionInfoRow label={<Trans>Frontend</Trans>} value={frontendVersion} />
                    <Divider sx={{ my: 1 }} />

                    <VersionInfoRow label={<Trans>Backend</Trans>} value={backendVersion || <Trans>Loading...</Trans>} />
                    <Divider sx={{ my: 1 }} />

                    <VersionInfoRow label={<Trans>Database</Trans>} value={alembicVersion || <Trans>Loading...</Trans>} />
                    <Divider sx={{ my: 1 }} />

                    <VersionInfoRow label={<Trans>Environment</Trans>} value={environmentTag} />
                    <Divider sx={{ my: 1 }} />

                    <VersionInfoRow label={<Trans>Analytics Database</Trans>} value={analyticsDatabase || <Trans>Loading...</Trans>} />
                </Box>
            </DialogContent>
        </Dialog>
    )
}
